/*
 * Multi-tier free-gift ladder — config-driven, IDEMPOTENT reconciler.
 *
 * The cart is the single source of truth. Every pass derives the gift lines the
 * cart SHOULD have from (config, paid subtotal) and issues the minimal cart
 * mutation to get there — it keeps no cross-pass "intent" state, so a failed
 * mutation simply self-heals on the next pass and there is no way to poison the
 * gift into a permanently-removed state. (Earlier this tracked a predicted
 * post-state + a dismissed set to honor manual deletes; that inference caused a
 * failed add to dismiss the gift forever and mis-flagged tier swaps as deletes.
 * The honest trade-off: in auto mode a deleted gift the shopper still qualifies
 * for is re-added. If opt-out is ever needed, add it as an explicit signal, not
 * an inference.)
 *
 *   mode "auto"     → the qualifying gift(s) are added automatically.
 *   mode "claim"    → a Claim button per qualifying gift adds it on click.
 *   mode "checkout" → nothing added here (the checkout / GoKwik adds it).
 *   exclusive true  → keep only the HIGHEST reached tier's gift; false → all reached.
 *
 * A gift line is removed the moment the cart no longer qualifies for it (auto:
 * not the desired set; claim/checkout: below its own tier), and any gift line a
 * shopper bumps past qty 1 is clamped back — the Function frees ONE unit, so an
 * un-clamped qty would charge for the extra. Pricing is the gift-tiers-discount
 * Function's job; exclusivity is ALSO enforced there (this is a display/UX
 * layer, the money boundary owns the offer's rules).
 *
 * Speed: reuses the pub/sub event's cart payload (no extra /cart.js on drawer
 * edits), one batched /cart/update.js for removals+qty-clamps and one
 * /cart/add.js for additions, section HTML on the final call only → one repaint.
 */
(function () {
  'use strict';
  if (window.__spendGiftInit) return;
  window.__spendGiftInit = true;

  var TAG = '__gift_tier';
  var OTHER_GIFT_TAGS = ['__spend_gift', '__bxgy_gift', '__gift_rule', '__product_gift'];
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTION = 'cart-drawer';
  var running = false;
  var queued = false;
  var queuedCart = null;
  // Last cart this script read from the server (fresh /cart.js). Lets the
  // add-to-cart fast path predict the new paid total without another read.
  var lastCart = null;

  function readConfig() {
    var el = document.querySelector('[data-spend-gift-config]');
    if (!el) return null;
    try {
      var c = JSON.parse(el.textContent);
      return c && Array.isArray(c.tiers) && c.tiers.length ? c : null;
    } catch (e) { return null; }
  }
  function isOtherGift(props) {
    if (!props) return false;
    for (var i = 0; i < OTHER_GIFT_TAGS.length; i++) if (props[OTHER_GIFT_TAGS[i]]) return true;
    return false;
  }
  // Rupees -> integer paise, matching the Function's Math.round so the JS gate
  // and the Function's threshold agree at fractional/boundary values.
  function thresholdCents(t) { return Math.round(Number(t) * 100); }

  function fetchCart() { return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }

  function addBatch(vids, withSections) {
    var props = {}; props[TAG] = '1';
    var items = vids.map(function (vid) { return { id: Number(vid), quantity: 1, properties: props }; });
    var body = { items: items };
    if (withSections) body.sections = [SECTION];
    return fetch(root + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }).then(function (res) { return res.ok ? res.json() : null; });
  }
  // Apply a line-key -> quantity map in one request (0 removes, 1 clamps).
  function updateBatch(updates, withSections) {
    var body = { updates: updates };
    if (withSections) body.sections = [SECTION];
    return fetch(root + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }).then(function (res) { return res.ok ? res.json() : null; });
  }

  function drawerHTMLOf(o) { return o && o.sections && o.sections[SECTION]; }
  function paintDrawer(html) {
    if (!html) return false;
    try {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('#CartDrawer');
      var live = document.getElementById('CartDrawer');
      var drawer = document.querySelector('cart-drawer');
      if (!fresh || !live) return false;
      // Adopt the already-parsed nodes (one parse) instead of innerHTML =
      // innerHTML (parse -> serialize -> parse).
      live.replaceChildren.apply(live, Array.prototype.slice.call(fresh.childNodes));
      var overlay = live.querySelector('#CartDrawer-Overlay');
      if (overlay && drawer && drawer.close) overlay.addEventListener('click', drawer.close.bind(drawer));
      var freshDrawer = parsed.querySelector('cart-drawer');
      if (drawer && freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      return true;
    } catch (e) { console.warn('[gift-tiers] paint failed', e); return false; }
  }
  function updateBubble(n) { if (n == null) return; var b = document.querySelector('.cart-count-bubble'); if (b) b.textContent = n; }

  // Optimistic bar paint: reflect the new milestone state (fill width + marker
  // active/locked) the instant the paid subtotal is known, before the cart
  // round-trip and authoritative re-render land — the round-trip is what made a
  // tier change feel slow. NUMERIC ONLY: fill % and marker states, never the
  // copy text, so this can never drift from Liquid's money formatting. Mirrors
  // spend-gift-bar.liquid's fill math (paid * 88 / topThreshold, capped 88) and
  // its marker rule (marker i is active when at least i+1 tiers are reached).
  // The authoritative full re-render overwrites the whole bar the moment it
  // arrives, so a wrong guess here self-corrects within one paint.
  // Piecewise fill, mirroring spend-gift-bar.liquid exactly: interpolate through the
  // marker control points (each tier's threshold → its marker %) so the coloured fill
  // lines up with the markers rather than running linearly to the top tier (which
  // under-fills the middle tiers). Markers are spaced by tier count: tier i of n sits
  // at (i+1)/n of the 88% line, so adding/removing a tier in the metafield re-spaces
  // the rail evenly (2 tiers → 44/88, 3 → 29/59/88). 2-dp, matching Liquid's round: 2.
  function barFillPercent(tiers, paidCents) {
    var n = tiers.length, prevThr = 0, prevPos = 0;
    for (var i = 0; i < n; i++) {
      var mpos = (i + 1) * 88 / n;
      var thrp = thresholdCents(tiers[i].threshold);
      if (paidCents < thrp) {
        var seg = thrp - prevThr;
        if (seg <= 0) return prevPos;
        var f = Math.round(((paidCents - prevThr) * (mpos - prevPos) / seg + prevPos) * 100) / 100;
        return f > 88 ? 88 : f;
      }
      prevThr = thrp; prevPos = mpos;
    }
    return prevPos; // paid >= top threshold
  }
  function paintBarOptimistic(cfg, paidCents) {
    var bar = document.querySelector('.gift-tiers[data-spend-gift]');
    if (!bar) return;
    var tiers = cfg.tiers, reached = 0;
    for (var i = 0; i < tiers.length; i++) if (paidCents >= thresholdCents(tiers[i].threshold)) reached++;
    var fillEl = bar.querySelector('.gift-tiers__fill');
    if (fillEl) fillEl.style.width = barFillPercent(tiers, paidCents) + '%';
    var markers = bar.querySelectorAll('.gift-tiers__marker');
    for (var m = 0; m < markers.length; m++) {
      var on = m < reached;
      markers[m].classList.toggle('is-active', on);
      markers[m].classList.toggle('is-locked', !on);
    }
  }

  function giftItemsTbody() {
    return document.querySelector('#CartDrawer-CartItems .cart-items tbody') ||
      document.querySelector('.cart-items tbody');
  }
  // Optimistic line swap: the instant a gift change is decided, reflect it in the
  // drawer's item table BEFORE the cart round-trip and authoritative re-render —
  // hide the outgoing gift row(s) and drop a lightweight placeholder for each
  // incoming one, so the shopper sees the swap start immediately instead of
  // waiting on the network. Returns revert(); callers invoke it on any pass that
  // does NOT end in a successful authoritative paint. On success the paint
  // replaces the whole subtree, so the optimistic nodes are already gone and
  // revert is a harmless no-op on detached nodes. No-ops (empty revert) when
  // there is no drawer table. Display only — the authoritative render stays the
  // single source of truth for the cart.
  // A placeholder cart row showing the ACTUAL gift so the swap looks instant.
  // It MIRRORS the theme's gift cart-item row (same `cart-item__*` classes and
  // the same 2-cell media+details structure), so the theme's own CSS sizes it —
  // 100px image, matching padding — and its height equals the authoritative
  // row's. That's what kills the layout shift: the real row replaces this one at
  // the same size. Couples to the theme's cart-item markup by design; if that
  // row's structure/classes change, update this to match. Label text is set via
  // textContent, never interpolated into HTML, so a merchant label can't break
  // markup.
  function makeGiftRow(g) {
    var free = !g.price || Number(g.price) <= 0;
    var tr = document.createElement('tr');
    tr.className = 'cart-item spend-gift-pending';
    tr.setAttribute('aria-busy', 'true');

    // Mirror the REAL gift row (cart-drawer.liquid) using the gift variant's own
    // data from the tier config (rowTitle / rowVariant / rowImg / rowOldPrice),
    // so the placeholder looks exactly like the row that replaces it. The old
    // tier label + marketing cut-out is only the fallback when that data is
    // missing (e.g. a tier whose product couldn't be resolved).
    var real = !!g.rowTitle;
    var tdMedia = document.createElement('td');
    tdMedia.className = 'cart-item__media';
    var imgSrc = real && g.rowImg ? g.rowImg : g.image;
    if (imgSrc) {
      var img = document.createElement('img');
      img.className = 'cart-item__image';
      img.src = imgSrc; img.alt = (real ? g.rowTitle : g.label) || 'Free gift';
      img.width = 100; img.height = 100;
      tdMedia.appendChild(img);
    }

    var tdDetails = document.createElement('td');
    tdDetails.className = 'cart-item__details';
    var top = document.createElement('div');
    top.className = 'cart-item__details-top';
    var info = document.createElement('div');
    info.className = 'cart-item__info';
    var name = document.createElement(real && g.rowUrl ? 'a' : 'span');
    name.className = 'cart-item__name text-color-green break text-[14px] font-IBM-medium leading-[20px]';
    if (real && g.rowUrl) name.href = g.rowUrl;
    name.textContent = (real ? g.rowTitle : g.label) || 'Free gift';
    info.appendChild(name);
    if (real && g.rowVariant) {
      var variant = document.createElement('div');
      variant.className = 'item-variants text-[12px] text-black tracking-[0.28px]';
      variant.textContent = g.rowVariant;
      info.appendChild(variant);
    }
    top.appendChild(info);
    if (real) {
      // Same element + classes as the real row's remove button, so the theme's
      // `cart-remove-button` CSS sizes it identically (20x20). A bare element
      // with the button classes rendered 92x44, squeezed the title onto two
      // lines and pushed the whole row 20px down until the real row landed.
      // Inert + no data-index: the custom element's click handler can't fire.
      var rm = document.createElement('cart-remove-button');
      rm.setAttribute('inert', '');
      rm.setAttribute('aria-hidden', 'true');
      rm.style.pointerEvents = 'none';
      rm.innerHTML = '<button type="button" class="button button--tertiary cart-remove-button" tabindex="-1"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.03558 3.59375H6.87933C6.96527 3.59375 7.03558 3.52344 7.03558 3.4375V3.59375H12.9731V3.4375C12.9731 3.52344 13.0434 3.59375 13.1293 3.59375H12.9731V5H14.3793V3.4375C14.3793 2.74805 13.8188 2.1875 13.1293 2.1875H6.87933C6.18988 2.1875 5.62933 2.74805 5.62933 3.4375V5H7.03558V3.59375ZM16.8793 5H3.12933C2.78363 5 2.50433 5.2793 2.50433 5.625V6.25C2.50433 6.33594 2.57465 6.40625 2.66058 6.40625H3.84027L4.32269 16.6211C4.35394 17.2871 4.90472 17.8125 5.57074 17.8125H14.4379C15.1059 17.8125 15.6547 17.2891 15.686 16.6211L16.1684 6.40625H17.3481C17.434 6.40625 17.5043 6.33594 17.5043 6.25V5.625C17.5043 5.2793 17.225 5 16.8793 5ZM14.2875 16.4062H5.72113L5.24847 6.40625H14.7602L14.2875 16.4062Z" fill="#949494"></path></svg></button>';
      top.appendChild(rm);
    }

    var bottom = document.createElement('div');
    bottom.className = 'cart-item__details-bottom';
    var badgeWrap = document.createElement('div');
    var badge = document.createElement('span');
    badge.className = 'gwp-gift-qty';
    badge.style.cssText = 'display:inline-block;font-size:1.2rem;font-weight:700;letter-spacing:.04em;color:#004E31;background:#EFF6EF;border-radius:.4rem;padding:.4rem .8rem;';
    badge.textContent = (free ? 'FREE GIFT' : 'GIFT') + ' · QTY 1';
    badgeWrap.appendChild(badge);
    var priceWrap = document.createElement('div');
    priceWrap.className = 'cart-item__price-wrapper';
    if (real && g.rowOldPrice) {
      var old = document.createElement('s');
      old.className = 'cart-item__old-price';
      old.textContent = g.rowOldPrice;
      priceWrap.appendChild(old);
      priceWrap.appendChild(document.createTextNode(' '));
    }
    var price = document.createElement('span');
    price.className = 'cart-item__sale-price';
    price.style.cssText = 'color:#004E31;font-weight:700;';
    price.textContent = free ? 'FREE' : ('₹' + Number(g.price).toLocaleString('en-IN'));
    priceWrap.appendChild(price);
    bottom.appendChild(badgeWrap);
    bottom.appendChild(priceWrap);

    tdDetails.appendChild(top);
    tdDetails.appendChild(bottom);
    if (real && free) {
      // The real row's discount tag ("Free gift"), so the row height matches.
      var ul = document.createElement('ul');
      ul.className = 'discounts list-unstyled';
      ul.innerHTML = '<li class="discounts__discount"><svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path></svg>Free gift</li>';
      tdDetails.appendChild(ul);
    }
    tr.appendChild(tdMedia);
    tr.appendChild(tdDetails);
    return tr;
  }
  function optimisticSwap(removeVids, addGifts) {
    var tbody = giftItemsTbody();
    if (!tbody) return function () {};
    var hidden = [], placeholders = [];
    for (var i = 0; i < removeVids.length; i++) {
      var row = tbody.querySelector('tr[data-gift-tier-variant="' + removeVids[i] + '"]');
      if (row) { hidden.push([row, row.style.display]); row.style.display = 'none'; }
    }
    // Insert at the TOP: Shopify places a newly added line at the top of the
    // cart, so the authoritative gift row lands there. Matching it means the
    // optimistic row is replaced in place — instead of appearing at the bottom
    // and then jumping to the top when the real row renders.
    for (var a = 0; a < addGifts.length; a++) {
      var tr = makeGiftRow(addGifts[a]);
      tbody.insertBefore(tr, tbody.firstChild);
      placeholders.push(tr);
    }
    return function revert() {
      for (var h = 0; h < hidden.length; h++) hidden[h][0].style.display = hidden[h][1];
      for (var p = 0; p < placeholders.length; p++) if (placeholders[p].parentNode) placeholders[p].parentNode.removeChild(placeholders[p]);
    };
  }

  function reachedTiers(tiers, paidCents) {
    var r = [];
    for (var i = 0; i < tiers.length; i++) if (paidCents >= thresholdCents(tiers[i].threshold)) r.push(tiers[i]);
    return r;
  }
  // The gift variant(s) the cart SHOULD have (auto mode's target set).
  function desiredVids(cfg, paidCents) {
    var reached = reachedTiers(cfg.tiers, paidCents);
    if (!reached.length) return [];
    if (cfg.exclusive === false) return reached.map(function (t) { return Number(t.variantId); });
    var best = reached[0];
    for (var i = 1; i < reached.length; i++) if (thresholdCents(reached[i].threshold) > thresholdCents(best.threshold)) best = reached[i];
    return [Number(best.variantId)];
  }

  // Usable directly when it carries line items + total (drawer-edit cartUpdate
  // payloads do; product-add payloads don't and we fetch instead).
  function usableCart(c) { return c && Array.isArray(c.items) && typeof c.total_price === 'number'; }

  function reconcile(cartArg) {
    if (running) {
      queued = true;
      // A newer USABLE payload supersedes; a newer unusable one invalidates the
      // queued snapshot so the drained pass re-fetches authoritative state.
      queuedCart = usableCart(cartArg) ? cartArg : null;
      return;
    }
    running = true;
    // Hoisted so the catch/finally can undo an optimistic swap that never got
    // an authoritative paint to replace it.
    var revertOptimistic = null;
    // Always decide on the LIVE cart. The event's cartData is a snapshot from
    // when the event fired; by the time this pass (or a queued one) runs, the
    // shopper may have removed/changed lines, and acting on the old snapshot
    // added a gift to a cart with no paid items and tried to zero lines that
    // were already gone (422 "updates parameter is invalid").
    fetchCart().then(function (cart) {
      if (usableCart(cart)) lastCart = cart;
      var cfg = readConfig();
      if (!cfg || !usableCart(cart)) return null;
      var mode = cfg.mode || 'auto';

      var thrByVid = {}, tierByVid = {};
      for (var t = 0; t < cfg.tiers.length; t++) {
        var tv = Number(cfg.tiers[t].variantId);
        thrByVid[tv] = thresholdCents(cfg.tiers[t].threshold);
        tierByVid[tv] = cfg.tiers[t];
      }

      var giftLines = [], giftCents = 0;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i], pr = it.properties || {};
        if (pr[TAG]) { giftLines.push({ key: it.key, vid: Number(it.variant_id), qty: it.quantity }); giftCents += it.final_line_price; continue; }
        if (isOtherGift(pr) || isExcludedLine(it, cfg)) giftCents += it.final_line_price;
      }
      var paidCents = cart.total_price - giftCents;

      // Instant milestone feedback — the bar reacts before the cart mutation and
      // re-render complete. Authoritative render (below) overwrites it.
      paintBarOptimistic(cfg, paidCents);

      var desired = desiredVids(cfg, paidCents);
      var present = giftLines.map(function (g) { return g.vid; });

      // updates: line key -> quantity. 0 removes; 1 clamps an inflated gift line.
      var updates = {};
      for (var g = 0; g < giftLines.length; g++) {
        var gl = giftLines[g];
        var keep = mode === 'auto'
          ? desired.indexOf(gl.vid) !== -1
          : paidCents >= (thrByVid[gl.vid] != null ? thrByVid[gl.vid] : Infinity); // claim/checkout: keep while its own tier holds
        if (!keep) updates[gl.key] = 0;
        else if (gl.qty > 1) updates[gl.key] = 1;
      }
      var addVids = [];
      if (mode === 'auto') for (var k = 0; k < desired.length; k++) if (present.indexOf(desired[k]) === -1) addVids.push(desired[k]);

      var updateKeys = Object.keys(updates);
      if (!updateKeys.length && !addVids.length) return null; // no-op: zero network

      // Optimistic: hide the gift row(s) about to be removed and placeholder the
      // one(s) about to be added, before the writes + render land. Only removals
      // (qty 0) hide a row; a qty clamp leaves the row in place.
      var removeVids = [];
      for (var rv = 0; rv < giftLines.length; rv++) if (updates[giftLines[rv].key] === 0) removeVids.push(giftLines[rv].vid);
      var addGifts = [];
      for (var ag = 0; ag < addVids.length; ag++) {
        var ti = tierByVid[addVids[ag]] || {};
        addGifts.push(ti);
      }
      revertOptimistic = optimisticSwap(removeVids, addGifts);

      var work;
      if (updateKeys.length) {
        // Remove/clamp first; only add if that succeeded — a failed update must
        // not leave a stale gift AND add a new one (two free gifts). A null
        // (non-2xx) is a hard failure: abort, self-heal next pass.
        work = updateBatch(updates, addVids.length === 0).then(function (r) {
          if (r === null) return null;
          return addVids.length ? addBatch(addVids, true) : r;
        });
      } else {
        work = addBatch(addVids, true);
      }
      return work.then(function (last) {
        var painted = false;
        var html = drawerHTMLOf(last);
        if (html) painted = paintDrawer(html);
        if (last && last.item_count != null) updateBubble(last.item_count);
        if (!painted) {
          // Authoritative DOM never landed — undo the optimistic swap so the
          // drawer reflects real server state, not the predicted one.
          if (revertOptimistic) revertOptimistic();
          // Cart PAGE (no drawer to paint): a server-side gift change leaves the
          // page DOM stale — reload so it matches server state. Rare surface.
          if (last && !document.querySelector('cart-drawer')) window.location.reload();
        }
      });
    }).catch(function (e) {
      if (revertOptimistic) revertOptimistic();
      console.warn('[gift-tiers] reconcile failed', e);
    }).then(function () {
      running = false;
      if (queued) { queued = false; var c = queuedCart; queuedCart = null; reconcile(c); }
    });
  }

  // ---- Add-to-cart fast path ------------------------------------------------
  // The normal pass waits for the theme's cartUpdate, re-reads /cart.js
  // (~0.4–0.6s, contended by the drawer re-render) and only then adds the gift
  // (~0.9s with the drawer section) — the gift row trailed the item by ~1.5s+.
  // Here, the moment a product add succeeds, predict the paid total from the
  // last cart we read + the lines just added and, if that newly earns a tier
  // with no gift in the cart yet, add it straight away. It runs as a normal
  // pass (running=true), and a verification pass with a FRESH cart read always
  // follows, so a wrong prediction (stale lastCart, other tab, discount) is
  // corrected exactly like any other drift. No gift is ever freed by this —
  // pricing stays with the gift-tiers-discount function.
  // Excluded products/variants never count toward a tier (config lists; the
  // rule lives in gift-engine.js, shared with the discount function's tests).
  function isExcludedLine(item, cfg) {
    var E = window.EmberGiftEngine;
    return !!(E && E.isExcluded && cfg && E.isExcluded(item.variant_id, item.handle, cfg));
  }
  function paidOf(cart) {
    var giftCents = 0, cfg = readConfig();
    for (var i = 0; i < cart.items.length; i++) {
      var pr = cart.items[i].properties || {};
      if (pr[TAG] || isOtherGift(pr) || isExcludedLine(cart.items[i], cfg)) giftCents += cart.items[i].final_line_price;
    }
    return cart.total_price - giftCents;
  }
  function fastPathAfterAdd(addResponse) {
    if (running || !usableCart(lastCart)) return;
    var cfg = readConfig();
    if (!cfg || (cfg.mode || 'auto') !== 'auto') return;
    for (var g = 0; g < lastCart.items.length; g++) if ((lastCart.items[g].properties || {})[TAG]) return; // swaps: leave to the normal pass
    var added = Array.isArray(addResponse && addResponse.items) ? addResponse.items : (addResponse && addResponse.id ? [addResponse] : []);
    var addedCents = 0;
    for (var a = 0; a < added.length; a++) {
      var pr = added[a].properties || {};
      if (pr[TAG] || isOtherGift(pr)) continue;
      if (typeof added[a].final_line_price === 'number') addedCents += added[a].final_line_price;
    }
    if (!addedCents) return;
    var desired = desiredVids(cfg, paidOf(lastCart) + addedCents);
    if (!desired.length) return;

    running = true;
    queued = true; // always verify against a fresh cart afterwards
    var tierByVid = {};
    for (var t = 0; t < cfg.tiers.length; t++) tierByVid[Number(cfg.tiers[t].variantId)] = cfg.tiers[t];
    var revert = optimisticSwap([], desired.map(function (vid) { return tierByVid[vid] || {}; }));
    addBatch(desired, true).then(function (last) {
      var html = drawerHTMLOf(last);
      var painted = html ? paintDrawer(html) : false;
      if (last && last.item_count != null) updateBubble(last.item_count);
      if (!painted && revert) revert();
    }).catch(function () {
      if (revert) revert();
    }).then(function () {
      running = false;
      if (queued) { queued = false; var c = queuedCart; queuedCart = null; reconcile(c); }
    });
  }
  // { items: [{ final_line_price }] } for a FormData product add, priced from
  // ShopifyAnalytics.meta.product (the page's own product), or null when the
  // variant isn't the page product (quick-add from a card) → body fallback.
  function predictFromForm(body) {
    try {
      if (typeof FormData === 'undefined' || !(body instanceof FormData)) return null;
      var vid = String(body.get('id') || '');
      var qty = parseInt(body.get('quantity') || '1', 10) || 1;
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      var variants = (meta && meta.variants) || [];
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === vid && typeof variants[i].price === 'number') {
          return { items: [{ final_line_price: variants[i].price * qty, properties: {} }] };
        }
      }
    } catch (e) {}
    return null;
  }

  function watchProductAdds() {
    if (!window.fetch || window.__spendGiftFetchWrapped) return;
    window.__spendGiftFetchWrapped = true;
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var p = nativeFetch.apply(this, arguments);
      try {
        var url = String((input && input.url) || input);
        var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        var body = init && init.body;
        var own = typeof body === 'string' && body.indexOf(TAG) !== -1; // this script's gift adds
        if (method === 'POST' && !own && /\/cart\/add(\.js)?(\?|$)/.test(url.replace(location.origin, ''))) {
          // Product-form adds post FormData. Price the added line from the
          // product data Shopify prints on the page so the gift can be added as
          // soon as the add is CONFIRMED (status), without waiting for the
          // response body — the theme asks for re-rendered sections in it, which
          // made it ~0.8s slower to arrive. Fallback: read the body.
          var predicted = predictFromForm(body);
          p.then(function (res) {
            if (!res || !res.ok) return;
            if (predicted) return fastPathAfterAdd(predicted);
            return res.clone().json().then(fastPathAfterAdd);
          }).catch(function () {});
        }
      } catch (e) {}
      return p;
    };
  }

  // Claim button (mode "claim"): add the clicked gift; reconcile keeps/removes it.
  function onClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-spend-gift-claim]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var vid = btn.getAttribute('data-variant-id');
    if (!vid) return;
    btn.disabled = true;
    addBatch([vid], true).then(function (res) {
      var html = drawerHTMLOf(res);
      if (html) paintDrawer(html);
      if (res) updateBubble(res.item_count);
    }).catch(function () { btn.disabled = false; });
  }

  function start() {
    document.addEventListener('click', onClick);
    // The cart service owns gift lines (adds with the item, swaps on change,
    // reconciles after other writes). Running this script's own add/reconcile
    // loop alongside it would race it — keep only the claim button.
    if (window.EmberCart) return;
    watchProductAdds();
    reconcile();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'spend-gift') return;
        reconcile(event && event.cartData);
      });
    }
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
